import forcedFunBody from './content/forced-fun-vs-five-minute-quiz-habit';
import icebreakersBody from './content/icebreakers-hostage-situation-slack-teams';
import newHireBody from './content/new-hire-scavenger-hunt-tools-not-people';
import birthdayBody from './content/nobody-owns-the-birthday';
import optionalBody from './content/optional-events-nobody-opts-in';
import proximityBody from './content/proximity-bias-hybrid-belonging';
import deadChannelBody from './content/slack-channel-died-after-kickoff';
import { blogCanonicalUrl, BLOG_PERMANENT_SLUGS } from './seo';

const BLOG_POSTS = [
  {
    slug: 'proximity-bias-hybrid-belonging',
    published: false,
    category: 'Hybrid culture',
    title: 'The Kitchen Has an A-Team. Remote Gets the Recap.',
    excerpt:
      'Office gets the jokes and kitchen updates. Remote gets the recap, late. What EX can run in Slack or Teams instead of an all-hands.',
    author: 'Casey / TryTrivia Content',
    date: 'September 23, 2026',
    readTime: '7 min read',
    featured: false,
    metaTitle: 'Proximity Bias Is Splitting Hybrid Teams',
    metaDescription:
      'Office gets the jokes and kitchen updates. Remote gets the recap, late. What EX can run in Slack or Teams instead of an all-hands.',
    image: '/blog/featured-office-kitchen-chat.jpg',
    imageAlt:
      'Two colleagues chatting with coffee mugs in an office kitchen, the in-person A-team by the kettle',
    imageCredit: 'Mikhail Nilov / Pexels',
    inArticleImage: '/blog/in-article-remote-laptop-home.jpg',
    inArticleAlt:
      'Remote worker sitting alone on a home sofa with a laptop, missing the kitchen conversation',
    inArticleCaption: 'Remote gets the recap. The kitchen already decided.',
    inArticleCredit: 'Artem Podrez / Pexels',
    related: [
      { label: 'Trivia for Slack', href: 'https://trytrivia.co/slack/' },
      { label: 'Trivia for Microsoft Teams', href: 'https://trytrivia.co/microsoft-teams/' },
      { label: 'Virtual Water Cooler', href: 'https://trytrivia.co/virtual-water-cooler/' },
      { label: 'Games', href: 'https://trytrivia.co/games/' }
    ],
    body: proximityBody
  },
  {
    slug: 'slack-channel-died-after-kickoff',
    published: false,
    category: 'Slack culture',
    title: 'The Slack Channel That Died After the Kickoff Emoji',
    excerpt:
      'HR launches #culture. Day one: 40 people and a VP heart. Day ten: a museum. Why Slack social channels die, and what to run where people already are.',
    author: 'Casey / TryTrivia Content',
    date: 'September 18, 2026',
    readTime: '7 min read',
    metaTitle: 'Why Slack Culture Channels Die After Kickoff',
    metaDescription:
      'HR launches #culture. Day one: 40 people and a VP heart. Day ten: a museum. Why Slack social channels die, and what to run where people already are.',
    image: '/blog/featured-empty-kickoff-room.jpg',
    imageAlt:
      'Empty conference room with rows of unused chairs facing a leftover kickoff slide on the projector',
    imageCredit: 'Pavel Danilyuk / Pexels',
    inArticleImage: '/blog/in-article-silent-unused-desks.jpg',
    inArticleAlt:
      'Two abandoned office workstations with black screens off, facing a grey window after everyone has left',
    inArticleCaption: 'Join count is a courtesy. A habit needs a recurring reason.',
    inArticleCredit: 'Diana ✨ / Pexels',
    related: [
      { label: 'Games', href: 'https://trytrivia.co/games/' },
      { label: 'Virtual Water Cooler', href: 'https://trytrivia.co/virtual-water-cooler/' },
      { label: 'Trivia for Slack', href: 'https://trytrivia.co/slack/' },
      { label: 'Trivia for Microsoft Teams', href: 'https://trytrivia.co/microsoft-teams/' }
    ],
    body: deadChannelBody
  },
  {
    slug: 'optional-events-nobody-opts-in',
    published: false,
    category: 'Team events',
    title: 'The Invite Says Optional. The RSVP List Still Feels Like a Test.',
    excerpt:
      'People decline optional lunch-and-learns and still feel watched. Kill RSVP theater. Run a 5-minute quiz in Slack or Teams instead.',
    author: 'Casey / TryTrivia Content',
    date: 'September 16, 2026',
    readTime: '7 min read',
    metaTitle: 'Why Optional Events Feel Like a Test',
    metaDescription:
      'People decline optional lunch-and-learns and still feel watched. Kill RSVP theater. Run a 5-minute quiz in Slack or Teams instead.',
    image: '/blog/featured-empty-conference-room.jpg',
    imageAlt:
      'Empty training room with rows of unused chairs and a blank purple screen, as if the optional lunch-and-learn had no turnout',
    imageCredit: 'Mikhail Nilov / Pexels',
    inArticleImage: '/blog/in-article-sad-salad-desk-lunch.jpg',
    inArticleAlt:
      'Woman eating a takeout salad alone at her desk beside an open laptop instead of joining the optional lunch',
    inArticleCaption: 'Optional was in the subject line. The scoreboard still printed.',
    inArticleCredit: 'Karolina Grabowska / Pexels',
    related: [
      { label: 'Games', href: 'https://trytrivia.co/games/' },
      { label: 'Trivia for Slack', href: 'https://trytrivia.co/slack/' },
      { label: 'Trivia for Microsoft Teams', href: 'https://trytrivia.co/microsoft-teams/' }
    ],
    body: optionalBody
  },
  {
    slug: 'new-hire-scavenger-hunt-tools-not-people',
    published: false,
    category: 'Onboarding',
    title: 'New-Hire Week Is a Scavenger Hunt of Tools, Not People',
    excerpt:
      'New-hire week is 14 logins, SSO, Notion, Jira, and zero humans. What HR can run in Slack or Teams in week one so people meet teammates without a speech.',
    author: 'Casey / TryTrivia Content',
    date: 'September 11, 2026',
    readTime: '7 min read',
    metaTitle: 'New-Hire Week Is a Tool Hunt, Not People',
    metaDescription:
      'New-hire week is 14 logins, SSO, Notion, Jira, and zero humans. What HR can run in Slack or Teams in week one so people meet teammates without a speech.',
    image: '/blog/featured-messy-desk-devices.jpg',
    imageAlt:
      'Unoccupied messy desk: dual screens, laptop, PC tower, and a nest of cables. Tools, not a welcome banner.',
    imageCredit: 'cottonbro studio / Pexels',
    inArticleImage: '/blog/in-article-headset-laptop.jpg',
    inArticleAlt:
      'One person at a laptop with over-ear headphones, figuring out logins and tools on their own.',
    inArticleCaption: 'Access is not a social network.',
    inArticleCredit: 'Karolina Grabowska / Pexels',
    related: [
      { label: 'Employee onboarding', href: 'https://trytrivia.co/employee-onboarding/' },
      { label: 'Know Your Team', href: 'https://trytrivia.co/know-your-team/' },
      { label: 'Games', href: 'https://trytrivia.co/games/' },
      { label: 'Trivia for Slack', href: 'https://trytrivia.co/slack/' }
    ],
    body: newHireBody
  },
  {
    slug: 'icebreakers-hostage-situation-slack-teams',
    published: true,
    featured: true,
    category: 'Icebreakers',
    title: 'Your Icebreaker Is a Hostage Situation. Play in the Channel Instead',
    excerpt:
      'Classic icebreakers put new hires on a stage. What HR should run in Slack or Teams instead: quizzes, Pictionary, and Know Your Team.',
    author: 'Casey / TryTrivia Content',
    date: 'September 7, 2026',
    readTime: '7 min read',
    metaTitle: "Icebreakers That Aren't Awkward for Hybrid Teams",
    metaDescription:
      'Classic icebreakers put new hires on a stage. What HR should run in Slack or Teams instead: quizzes, Pictionary, and Know Your Team.',
    image: '/blog/featured-awkward-video-call-waiting-faces.jpg',
    imageAlt:
      'New hire at a laptop watching a video-call grid of three colleagues with weary, waiting faces — round-robin icebreaker, not a happy circle',
    imageCredit: 'Anna Shvets / Pexels',
    inArticleImage: '/blog/in-article-typing-in-channel.jpg',
    inArticleAlt:
      'Overhead view of hands typing on a laptop at a quiet desk — joining a shared channel game, not confessing a fun fact on camera',
    inArticleCaption: 'Shared play works because the thing in the middle is the game, not the person.',
    inArticleCredit: 'cottonbro studio / Pexels',
    related: [
      { label: 'Games', href: 'https://trytrivia.co/games/' },
      { label: 'Know Your Team', href: 'https://trytrivia.co/know-your-team/' },
      { label: 'Trivia for Slack', href: 'https://trytrivia.co/slack/' },
      { label: 'Trivia for Microsoft Teams', href: 'https://trytrivia.co/microsoft-teams/' }
    ],
    body: icebreakersBody
  },
  {
    slug: 'forced-fun-vs-five-minute-quiz-habit',
    published: true,
    featured: false,
    category: 'Team rituals',
    title: "Forced Fun Dies in Week Three. A 5-Minute Quiz Habit Doesn't",
    excerpt:
      'Culture programs fade by week three. How HR builds an opt-in, async, 5-minute quiz habit in Slack or Teams that quiet people can join.',
    author: 'Casey / TryTrivia Content',
    date: 'September 7, 2026',
    readTime: '7 min read',
    metaTitle: 'Why Forced Fun Dies After Week Three',
    metaDescription:
      'Culture programs fade by week three. How HR builds an opt-in, async, 5-minute quiz habit in Slack or Teams that quiet people can join.',
    image: '/blog/featured-leftover-office-party.jpg',
    imageAlt:
      'Empty office party table: unused party hats, unopened champagne, pie, and stacked red cups in front of a vacant desk — #fun-friday that nobody stayed for',
    imageCredit: 'RDNE Stock project / Pexels',
    inArticleImage: '/blog/in-article-quiet-five-minute-habit.jpg',
    inArticleAlt:
      'Quiet person on a sofa with a laptop and a mug, joining an optional five-minute quiz on her own time — not a balloon arch',
    inArticleCaption: 'Events need hype. Habits need a slot that already exists.',
    inArticleCredit: 'Vlada Karpovich / Pexels',
    related: [
      { label: 'Games', href: 'https://trytrivia.co/games/' },
      { label: 'Virtual Water Cooler', href: 'https://trytrivia.co/virtual-water-cooler/' },
      { label: 'Trivia for Slack', href: 'https://trytrivia.co/slack/' },
      { label: 'Trivia for Microsoft Teams', href: 'https://trytrivia.co/microsoft-teams/' }
    ],
    body: forcedFunBody
  },
  {
    slug: 'nobody-owns-the-birthday',
    published: true,
    category: 'Employee milestones',
    title: "Nobody Owns the Birthday. That's Why It Gets Missed.",
    excerpt:
      'When the person who posts in #general is OOO, the anniversary still happens. Missed milestones are a belonging failure, not a sheet problem.',
    author: 'Casey / TryTrivia Content',
    date: 'September 1, 2026',
    readTime: '7 min read',
    metaTitle: "Nobody Owns Birthdays. That's Why They Get Missed",
    metaDescription:
      'When the person who posts in #general is OOO, the anniversary still happens. Missed milestones are a belonging failure, not a sheet problem.',
    image: '/blog/featured-leftover-birthday-cake.jpg',
    imageAlt:
      'Leftover birthday cake with a slice already gone, unlit candles, cupcakes, confetti, and party hats — no people, no huge office party',
    imageCredit: 'PNW Production / Pexels',
    inArticleImage: '/blog/in-article-one-person-supposed-to-post.jpg',
    inArticleAlt:
      'One person at a laptop who was supposed to post the birthday in #general, typing alone with a blank screen',
    inArticleCaption: 'Guilt is not an operating system.',
    inArticleCredit: 'Yan Krukau / Pexels',
    related: [
      { label: 'Employee milestones', href: 'https://trytrivia.co/employee-milestones/' },
      { label: 'Trivia for Slack', href: 'https://trytrivia.co/slack/' },
      { label: 'Trivia for Microsoft Teams', href: 'https://trytrivia.co/microsoft-teams/' },
      { label: 'Custom quizzes', href: 'https://trytrivia.co/games/custom-quiz/' },
      { label: 'Games', href: 'https://trytrivia.co/games/' }
    ],
    body: birthdayBody
  },
  {
    slug: 'stop-zoom-happy-hour-bond-in-slack-teams',
    published: true,
    category: 'Team culture',
    title: 'Stop Booking Another Zoom Happy Hour. Bond in Slack or Teams Instead',
    excerpt:
      'Practical Slack and Teams bonding ideas that take five minutes, skip the calendar guilt, and feel less forced.',
    author: 'Casey / TryTrivia Content',
    date: 'August 22, 2026',
    readTime: '8 min read',
    metaTitle: 'Zoom Happy Hour Alternatives for Hybrid Teams',
    metaDescription:
      'Tired of empty Zoom happy hours? Practical Slack and Teams bonding ideas that take five minutes, skip the calendar guilt, and feel less forced.',
    image:
      'https://images.pexels.com/photos/7709166/pexels-photo-7709166.jpeg?auto=compress&cs=tinysrgb&w=1600',
    imageAlt: 'Laptops and headsets on a table',
    legacyComponent: true
  }
];

export const getBlogPost = (slug) => BLOG_POSTS.find((post) => post.slug === slug);

export const getPublishedPosts = () =>
  BLOG_POSTS.filter((post) => post.published).sort((a, b) => new Date(b.date) - new Date(a.date));

export const getFeaturedPost = () =>
  getPublishedPosts().find((post) => post.featured) || getPublishedPosts()[0];

export { blogCanonicalUrl, BLOG_PERMANENT_SLUGS };

export default BLOG_POSTS;
